'use client';

import { useState, useRef, useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';
import SuggestedQuestions from './SuggestedQuestions';
import CitationCard, { type Source } from './CitationCard';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
}

export default function ChatInterface() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function sendMessage(query: string) {
    if (!query.trim() || loading) return;
    setInput('');
    setLoading(true);

    const userMsg: Message = { role: 'user', content: query };
    setMessages(prev => [...prev, userMsg]);

    const history = messages.map(m => ({ role: m.role, content: m.content }));

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, history }),
      });

      const sourcesHeader = res.headers.get('X-Sources');
      const sources: Source[] = sourcesHeader ? JSON.parse(decodeURIComponent(sourcesHeader)) : [];

      if (!res.body) throw new Error('No response body');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let answer = '';

      const assistantMsg: Message = { role: 'assistant', content: '', sources };
      setMessages(prev => [...prev, assistantMsg]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        // Parse SSE format from Workers AI
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') continue;
            try {
              const parsed = JSON.parse(data) as { response?: string };
              if (parsed.response) {
                answer += parsed.response;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { ...assistantMsg, content: answer, sources };
                  return updated;
                });
              }
            } catch {}
          }
        }
      }
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'エラーが発生しました。もう一度お試しください。' },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-8">
          {messages.length === 0 && (
            <div className="fade-in text-center pt-12 pb-8">
              <h1
                style={{ color: 'var(--color-text)', letterSpacing: '0.2em' }}
                className="text-2xl font-light mb-2"
              >
                ハンドメイド知識庫
              </h1>
              <p style={{ color: 'var(--color-muted)', letterSpacing: '0.1em' }} className="text-sm mb-12">
                動画の内容に基づいてお答えします
              </p>
              <SuggestedQuestions onSelect={q => sendMessage(q)} />
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`fade-in flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-1' : ''}`}>
                {msg.role === 'user' ? (
                  <div
                    style={{
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text)',
                    }}
                    className="px-5 py-3 rounded-2xl rounded-tr-sm text-sm leading-relaxed"
                  >
                    {msg.content}
                  </div>
                ) : (
                  <div>
                    <div
                      style={{ color: 'var(--color-body)' }}
                      className="text-sm leading-relaxed whitespace-pre-wrap"
                    >
                      {msg.content}
                      {loading && i === messages.length - 1 && (
                        <span
                          style={{ backgroundColor: 'var(--color-accent)' }}
                          className="inline-block w-0.5 h-4 ml-0.5 animate-pulse"
                        />
                      )}
                    </div>
                    {msg.sources && msg.sources.length > 0 && !loading && (
                      <CitationCard sources={msg.sources} />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input */}
      <div
        style={{ borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg)' }}
        className="px-4 py-4"
      >
        <div className="max-w-2xl mx-auto">
          <div
            style={{ border: '1px solid var(--color-border)' }}
            className="flex items-end gap-3 rounded-2xl px-4 py-3 focus-within:border-[var(--color-accent)] transition-colors duration-300"
          >
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(input);
                }
              }}
              placeholder="ハンドメイドについて質問してください..."
              rows={1}
              style={{ color: 'var(--color-text)', resize: 'none' }}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--color-muted)] leading-relaxed"
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={loading || !input.trim()}
              style={{
                backgroundColor: input.trim() && !loading ? 'var(--color-accent)' : 'var(--color-border)',
                color: input.trim() && !loading ? 'white' : 'var(--color-muted)',
              }}
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
          <p style={{ color: 'var(--color-muted)' }} className="text-xs mt-2 text-center">
            Enterで送信 / Shift+Enterで改行
          </p>
        </div>
      </div>
    </div>
  );
}
